/**
 * Shared Google Maps helper utilities for Inward Session maps.
 * Used by InwardPreview, InwardSessionDetail, and PharmeasyHubOps.
 */

// Browser Maps key — inlined into the bundle at build time, so not secret.
// Value comes from REACT_APP_GOOGLE_MAPS_KEY (.env.local); protection is enforced
// by HTTP-referrer + API restrictions on the key in Google Cloud Console.
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Trip color palette for overall session maps (Google Static Maps marker colors)
export const TRIP_COLORS = [
  "red", "blue", "green", "purple", "orange", "yellow", "0xE91E63", "0x00BCD4",
  "0x795548", "0x607D8B", "0xFF5722", "0x4CAF50"
];

// Hex colors for UI badges/legends matching the above
export const TRIP_HEX_COLORS = [
  "#EF4444", "#3B82F6", "#22C55E", "#A855F7", "#F97316", "#EAB308", "#E91E63", "#00BCD4",
  "#795548", "#607D8B", "#FF5722", "#4CAF50"
];

/**
 * Extract coordinates from a shipment object.
 * Handles both wizard shipment format and session detail format.
 */
export function extractShipmentCoords(shipment) {
  // Session detail format
  const calc = shipment?.calculated_dest_address_coordinate;
  if (calc?.success && calc?.coordinates) {
    return { lat: calc.coordinates.lat, lng: calc.coordinates.lng };
  }
  // Wizard format (from create-trips response)
  const destCoord = shipment?.calculatedDestAddressCoordinate;
  if (destCoord?.success && destCoord?.coordinates) {
    return { lat: destCoord.coordinates.lat, lng: destCoord.coordinates.lng };
  }
  // Fallback: destAddress lat/lng
  const dest = shipment?.destAddress || shipment?.dest_address;
  if (dest?.latitude && dest?.longitude) {
    const lat = parseFloat(dest.latitude);
    const lng = parseFloat(dest.longitude);
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }
  return null;
}

/**
 * Build Google Maps Embed URL with directions for a single trip.
 * Caps intermediate waypoints at 20 (Embed API limit).
 * @param {Array} shipments
 * @param {Object} [hubCoords] - { lat, lng } - if provided, used as route origin
 */
export function buildDirectionsEmbedUrl(shipments, hubCoords = null) {
  const waypoints = shipments
    .map(s => {
      const coords = extractShipmentCoords(s);
      return coords ? `${coords.lat},${coords.lng}` : null;
    })
    .filter(Boolean);

  if (waypoints.length === 0) return null;

  // If hub coords provided, hub is origin and first delivery point becomes a waypoint
  const origin = hubCoords?.lat && hubCoords?.lng
    ? `${hubCoords.lat},${hubCoords.lng}`
    : waypoints[0];
  const destination = waypoints[waypoints.length - 1];
  const intermediate = hubCoords?.lat && hubCoords?.lng
    ? waypoints.slice(0, -1)   // all except last are intermediate (hub is origin)
    : waypoints.slice(1, -1);  // skip first (origin) and last (destination)
  const capped = intermediate.length > 20
    ? intermediate.filter((_, i) => i % Math.ceil(intermediate.length / 20) === 0).slice(0, 20)
    : intermediate;
  const waypointsStr = capped.join("|");

  let url = `https://www.google.com/maps/embed/v1/directions?key=${GOOGLE_MAPS_KEY}&origin=${origin}&destination=${destination}`;
  if (waypointsStr) url += `&waypoints=${waypointsStr}`;
  url += "&mode=driving";
  return url;
}

/**
 * Build Google Maps Static Map URL for a single trip.
 */
export function buildStaticMapUrl(shipments, zoom = 12) {
  const markers = shipments
    .map((s, idx) => {
      const coords = extractShipmentCoords(s);
      return coords ? `markers=color:red%7Clabel:${idx + 1}%7C${coords.lat},${coords.lng}` : null;
    })
    .filter(Boolean);

  if (markers.length === 0) return null;

  let totalLat = 0, totalLng = 0, count = 0;
  shipments.forEach(s => {
    const coords = extractShipmentCoords(s);
    if (coords) { totalLat += coords.lat; totalLng += coords.lng; count++; }
  });
  const centerLat = totalLat / count;
  const centerLng = totalLng / count;

  return `https://maps.googleapis.com/maps/api/staticmap?center=${centerLat},${centerLng}&zoom=${zoom}&size=800x400&maptype=roadmap&${markers.join("&")}&key=${GOOGLE_MAPS_KEY}`;
}

/**
 * Build an interactive Google Maps HTML blob for the overall session map.
 * Returns a blob URL that can be used as an iframe src.
 * Each trip gets differently colored markers with labels.
 * Optionally renders a hub marker if hubInfo is provided.
 * @param {Array} trips - Array of trip objects
 * @param {Object} [hubInfo] - { lat, lng, name, address }
 */
export function buildOverallSessionMapBlobUrl(trips, hubInfo = null) {
  let totalLat = 0, totalLng = 0, count = 0;
  const tripMarkerData = [];

  trips.forEach((trip, tripIdx) => {
    const color = TRIP_HEX_COLORS[tripIdx % TRIP_HEX_COLORS.length];
    const shipments = trip.sublocation_assignments || trip.shipments || [];
    const markers = [];
    shipments.forEach(s => {
      const coords = extractShipmentCoords(s);
      if (coords) {
        markers.push({ lat: coords.lat, lng: coords.lng, code: s.shipment_code || s.shipmentId || "" });
        totalLat += coords.lat;
        totalLng += coords.lng;
        count++;
      }
    });
    if (markers.length > 0) {
      tripMarkerData.push({
        tripNum: trip.trip_number || tripIdx + 1,
        color,
        markers,
      });
    }
  });

  // Include hub in center calculation if available
  const hubData = hubInfo?.lat && hubInfo?.lng ? hubInfo : null;
  if (hubData) {
    totalLat += hubData.lat;
    totalLng += hubData.lng;
    count++;
  }

  if (count === 0) return null;

  const centerLat = totalLat / count;
  const centerLng = totalLng / count;

  const html = `<!DOCTYPE html>
<html><head>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body,#map{width:100%;height:100%}
  .legend{position:absolute;top:10px;left:10px;background:#fff;padding:10px 14px;border-radius:10px;
    box-shadow:0 2px 8px rgba(0,0,0,.15);font-family:system-ui,sans-serif;max-height:260px;overflow-y:auto;z-index:5}
  .legend h4{font-size:12px;color:#475569;margin-bottom:6px}
  .legend-item{display:flex;align-items:center;gap:6px;padding:2px 0;font-size:11px;color:#334155}
  .legend-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}
  .legend-hub{width:10px;height:10px;flex-shrink:0}
</style>
</head><body>
<div id="map"></div>
<div class="legend" id="legend"><h4>Trip Legend</h4></div>
<script>
const TRIPS = ${JSON.stringify(tripMarkerData)};
const CENTER = {lat:${centerLat},lng:${centerLng}};
const HUB = ${hubData ? JSON.stringify(hubData) : 'null'};

function initMap(){
  const map = new google.maps.Map(document.getElementById('map'),{
    center:CENTER, zoom:12, mapTypeControl:false, streetViewControl:false,
    fullscreenControl:true, zoomControl:true
  });
  const bounds = new google.maps.LatLngBounds();
  const legend = document.getElementById('legend');

  // Hub marker
  if(HUB){
    const hubPos = {lat:HUB.lat,lng:HUB.lng};
    bounds.extend(hubPos);
    const hubMarker = new google.maps.Marker({
      position: hubPos, map,
      icon: {
        url: 'https://maps.google.com/mapfiles/ms/icons/homegardenbusiness.png',
        scaledSize: new google.maps.Size(36,36),
        anchor: new google.maps.Point(18,36)
      },
      title: (HUB.name||'Hub') + (HUB.address?' - '+HUB.address:''),
      zIndex: 9999
    });
    const hubInfo = new google.maps.InfoWindow({
      content:'<div style="font-family:system-ui;font-size:12px;max-width:220px"><b>'+(HUB.name||'Hub')+'</b>'+(HUB.address?'<br><span style="color:#64748b">'+HUB.address+'</span>':'')+'</div>'
    });
    hubMarker.addListener('click',()=>hubInfo.open(map,hubMarker));

    const hubLegend = document.createElement('div');
    hubLegend.className='legend-item';
    hubLegend.innerHTML='<img src="https://maps.google.com/mapfiles/ms/icons/homegardenbusiness.png" class="legend-hub" style="width:14px;height:14px"/> Hub';
    legend.appendChild(hubLegend);
  }

  TRIPS.forEach(trip => {
    const div = document.createElement('div');
    div.className='legend-item';
    div.innerHTML='<span class="legend-dot" style="background:'+trip.color+'"></span>Trip #'+trip.tripNum+' ('+trip.markers.length+')';
    legend.appendChild(div);

    trip.markers.forEach((m, i) => {
      const pos = {lat:m.lat,lng:m.lng};
      bounds.extend(pos);
      const marker = new google.maps.Marker({
        position: pos, map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: trip.color, fillOpacity: 0.9,
          strokeColor: '#fff', strokeWeight: 1.5, scale: 8
        },
        title: 'Trip #'+trip.tripNum+' - '+(m.code||'#'+(i+1))
      });
      const infoWindow = new google.maps.InfoWindow({
        content:'<div style="font-family:system-ui;font-size:12px"><b>Trip #'+trip.tripNum+'</b><br>'+
          (m.code?'Shipment: '+m.code+'<br>':'')+
          'Seq: '+(i+1)+'/'+trip.markers.length+'</div>'
      });
      marker.addListener('click',()=>infoWindow.open(map,marker));
    });
  });
  map.fitBounds(bounds, {top:50,bottom:20,left:50,right:20});
}
</script>
<script async src="https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&callback=initMap"></script>
</body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  return URL.createObjectURL(blob);
}

/**
 * Build a "Open in Google Maps" link for a route with all waypoints.
 * @param {Array} shipments
 * @param {Object} [hubCoords] - { lat, lng } - if provided, prepended as route start
 */
export function buildGoogleMapsRouteLink(shipments, hubCoords = null) {
  const coords = shipments
    .map(s => {
      const c = extractShipmentCoords(s);
      return c ? `${c.lat},${c.lng}` : null;
    })
    .filter(Boolean);
  if (coords.length === 0) return null;
  const allPoints = hubCoords?.lat && hubCoords?.lng
    ? [`${hubCoords.lat},${hubCoords.lng}`, ...coords]
    : coords;
  return `https://www.google.com/maps/dir/${allPoints.join("/")}`;
}
